import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, ArrowRight, Info, X, CalendarClock, MoveRight, MessageSquareText } from 'lucide-react';
import { toast } from '../../../common/Toast/toast';
import PopupDone, { usePopupDone } from '../../../common/PopupDone/PopupDone';
import AsyncPagedSelect from '../../../../apps/ClassroomApp/components/AsyncPagedSelect.jsx';
import { getBatches } from '../../../../../services/BatchService';
import { createBatchChangeRequest, extractBatchChangeErrorMessage } from '../../../../../services/BatchChangeRequestService';

const getCourseIdOf = (batch) => {
  if (!batch) return null;
  if (Array.isArray(batch?.courseId) && batch.courseId.length > 0) {
    const first = batch.courseId[0];
    return String(first?._id || first?.id || (typeof first === 'string' ? first : ''));
  }
  if (typeof batch?.courseId === 'object' && batch?.courseId !== null) {
    return String(batch.courseId._id || batch.courseId.id || '');
  }
  if (batch?.courseId) {
    return String(batch.courseId);
  }
  if (batch?.courseDetails) {
    return String(batch.courseDetails._id || batch.courseDetails.id || '');
  }
  return null;
};

const getCourseNameOf = (batch) =>
  Array.isArray(batch?.courseId) && batch.courseId.length > 0
    ? (batch.courseId[0]?.name || batch.courseId[0]?.courseName)
    : (batch?.courseDetails?.name || batch?.courseDetails?.courseName);

/**
 * Lets a student file a request to move from one of their current batches to
 * another batch of the same course. `myBatches` is the small, already-loaded
 * list of batches the student is enrolled in (no need to paginate that); the
 * target batch is normally picked from a real server-paginated/searched
 * dropdown scoped to the chosen course, since a course can have many
 * batches.
 *
 * If `presetToBatch` ({ id, name, courseId }) is passed — e.g. the student
 * clicked "Request to switch" on a specific row in Suggested Batches — the
 * "To batch" step is skipped entirely: it's shown read-only, and "From
 * batch" is narrowed to only the batches they already have in that same
 * course (a switch needs an existing batch in that course to switch FROM;
 * there's nothing to pick otherwise).
 */
const RequestBatchChangeModal = ({ show, onClose, myBatches = [], studentId, onSubmitted, presetToBatch = null }) => {
  const [fromBatchId, setFromBatchId] = useState('');
  const [toBatchId, setToBatchId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // A sent request is confirmed inside the dialog (PopupDone), not by a toast.
  const { done } = usePopupDone(Boolean(show));

  const fromBatchOptions = useMemo(() => {
    if (!presetToBatch || !presetToBatch.courseId) return myBatches;
    const targetCourseId = String(presetToBatch.courseId);
    const matched = myBatches.filter((b) => {
      const bCourseId = getCourseIdOf(b);
      return bCourseId && bCourseId === targetCourseId;
    });
    return matched.length > 0 ? matched : myBatches;
  }, [myBatches, presetToBatch]);

  const fromBatch = useMemo(
    () => fromBatchOptions.find((b) => (b._id || b.id) === fromBatchId) || null,
    [fromBatchOptions, fromBatchId]
  );
  const courseId = presetToBatch ? presetToBatch.courseId : (fromBatch ? getCourseIdOf(fromBatch) : null);
  const courseName = fromBatch ? getCourseNameOf(fromBatch) : null;

  // No current batch in this course at all (only possible in preset mode,
  // clicked from Suggested Batches for a course the student's purchased but
  // never been scheduled into) — nothing to "switch" from, so this becomes
  // a join request instead: no fromBatch, just the preset toBatch + reason.
  const isJoinRequest = Boolean(presetToBatch) && fromBatchOptions.length === 0;

  // Preset mode: as soon as the modal opens, lock "To batch" to the preset
  // and pre-select "From batch" if there's exactly one candidate.
  useEffect(() => {
    if (!show) return;
    if (presetToBatch) {
      setToBatchId(presetToBatch.id);
      setFromBatchId(fromBatchOptions.length === 1 ? (fromBatchOptions[0]._id || fromBatchOptions[0].id) : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, presetToBatch?.id]);

  const resetAndClose = () => {
    setFromBatchId('');
    setToBatchId('');
    setReason('');
    onClose();
  };

  const handleFromBatchChange = (e) => {
    setFromBatchId(e.target.value);
    if (!presetToBatch) setToBatchId(''); // course changed underneath — target selection no longer valid
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!toBatchId || (!isJoinRequest && !fromBatchId)) return;
    setSubmitting(true);
    try {
      await createBatchChangeRequest({
        studentId,
        courseId,
        // Omitted entirely for a join — the backend treats a missing
        // fromBatchId as "this student has no current batch for the
        // course", which is exactly what's true here.
        ...(isJoinRequest ? {} : { fromBatchId }),
        toBatchId,
        reason: reason.trim(),
      });
      toast.success(isJoinRequest
          ? "Your request to join this batch has been sent for approval."
          : "Your batch change request has been sent for approval.");
      // The dialog shows the sent message, then closes itself; the sent reason is cleared.
      setReason('');
      onSubmitted && onSubmitted();
    } catch (error) {
      const message = extractBatchChangeErrorMessage(error, 'Could not submit your request. Please try again.');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const toBatchLabel = presetToBatch
    ? presetToBatch.name
    : null;

  return (
    /* The modal renders inside the card, which is inside the page's
       .dashboard-wrapper, so the scoped .bcm rules reach it. It must not
       carry that class itself — the rules are descendant selectors and
       would stop matching.

       Written as a plain block comment, not {/* … *\/}: this is a ternary
       branch, an expression position, where a JSX comment is a syntax
       error. */
    show ? (
        <div className="bcm" role="dialog" aria-modal="true" onClick={resetAndClose}>
          <div className="bcm-panel" onClick={(e) => e.stopPropagation()}>
      <div className="bcm-head">
        <div className="bcm-mark">
          <ArrowRightLeft size={20} aria-hidden="true" />
        </div>
        <div className="bcm-head-text">
          <div className="bcm-title">{isJoinRequest ? 'Request to Join' : 'Request Batch Change'}</div>
          <div className="bcm-sub">
            {isJoinRequest ? "Get scheduled into this batch for the first time" : 'Move to a different schedule for the same course'}
          </div>
        </div>
        <button type="button" className="bcm-close" onClick={resetAndClose} aria-label="Close">
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      {done ? (
        <PopupDone message={done} onClose={resetAndClose} />
      ) : (
      <form className="bcm-form" onSubmit={handleSubmit}>
        <div className="bcm-body">
          <div className="bcm-info">
            <Info size={15} aria-hidden="true" />
            <span>
              {isJoinRequest
                ? "You don't have a batch in this course yet — this requests to be scheduled into it for the first time. Needs approval from your coordinator."
                : "Moving batches needs approval from your coordinator — nothing changes until it's approved."}
            </span>
          </div>

          {!isJoinRequest && (fromBatch || presetToBatch) && toBatchId && (
            <div className="bcm-move">
              <span>{fromBatch ? fromBatch.name : '…'}</span>
              <MoveRight size={14} aria-hidden="true" />
              <span>{toBatchLabel || '…'}</span>
            </div>
          )}

          {!isJoinRequest && (
            <div className="bcm-field">
              <label htmlFor="bcm-from-batch"><CalendarClock size={14} aria-hidden="true" /> From batch</label>
              <select id="bcm-from-batch" name="fromBatch" value={fromBatchId} onChange={handleFromBatchChange} required>
                <option value="">Select your current batch…</option>
                {fromBatchOptions.map((b) => {
                  const id = b._id || b.id;
                  return (
                    <option key={id} value={id}>
                      {b.name} {getCourseNameOf(b) ? `— ${getCourseNameOf(b)}` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {!isJoinRequest && fromBatch && !presetToBatch && (
            <div className="bcm-hint">
              <ArrowRight size={13} aria-hidden="true" />
              <span>now choose the target batch below</span>
            </div>
          )}

          {/* --menu lifts this field over the ones below it, so the
              dropdown's absolutely-positioned menu is not painted under
              the Reason field. */}
          <div className="bcm-field bcm-field--menu">
            <span className="bcm-label" id="bcm-to-batch-label"><ArrowRightLeft size={14} aria-hidden="true" /> {isJoinRequest ? 'Batch to join' : 'To batch'}</span>
            {presetToBatch ? (
              <div className="bcm-preset">
                <span>{presetToBatch.name}</span>
                <span className="bcm-preset-tag">Selected</span>
              </div>
            ) : (
              <>
                <AsyncPagedSelect
                  key={courseId || 'no-course'}
                  labelId="bcm-to-batch-label"
                  value={toBatchId}
                  onChange={setToBatchId}
                  isDisabled={!courseId}
                  placeholder={courseId ? 'Search batches…' : 'Pick a "From batch" first'}
                  fetchPage={async (page, search) => {
                    const res = await getBatches(20, page, search, 'name', null, courseId);
                    return {
                      data: (res.data || []).filter((b) => (b._id || b.id) !== fromBatchId),
                      pagination: res.pagination,
                    };
                  }}
                  toOption={(b) => ({ value: b._id || b.id, label: b.name })}
                />
                {courseName && (
                  <div className="bcm-hint">
                    Showing other batches for {courseName}.
                  </div>
                )}
              </>
            )}
          </div>

          <div className="bcm-field">
            <label htmlFor="bcm-reason"><MessageSquareText size={14} aria-hidden="true" /> Reason (optional)</label>
            <textarea
              id="bcm-reason"
              name="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why do you want to switch batches?"
              maxLength={500}
            />
          </div>
        </div>

        <div className="bcm-foot">
          <button
            type="button"
            className="bcm-btn"
            onClick={resetAndClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bcm-btn bcm-btn--go"
            disabled={!toBatchId || (!isJoinRequest && !fromBatchId) || submitting}
          >
            {submitting ? 'Submitting…' : isJoinRequest ? 'Request to Join' : 'Submit Request'}
          </button>
        </div>
      </form>
      )}
    </div>
        </div>
      ) : null
  );
};

export default RequestBatchChangeModal;
